/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AiringOfferService } from "@tivocorp/data-domain-lib";
import type { AiringOffer } from "client-code/airing";

import type { IAiringOfferInteractor } from "../../interfaces/IAiringOfferInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link AiringOfferService}.
 *
 * Composed by feature interactors that need linear airing data
 * (Playback, Guide, ContentDetails, …). The
 * {@link ApplicationFeatureArea} is fixed at construction time.
 *
 * Stateless: a fresh `AiringOfferService` is built per call via
 * {@link ServiceLocator}.
 */
export class AiringOfferInteractor
  extends BaseInteractor
  implements IAiringOfferInteractor
{
  private static readonly TAG: string = "AiringOfferInteractor";

  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  public async fetchAiringOffers(
    stationId: string,
    playerCurrentTime?: number,
    options?: TNetworkOptions
  ): Promise<Array<AiringOffer>> {
    return this.executeGuarded(
      "fetchAiringOffers",
      async (): Promise<Array<AiringOffer>> => {
        Log.d(AiringOfferInteractor.TAG, "fetchAiringOffers() called");
        const service: AiringOfferService = ServiceLocator.getInstance(
          AiringOfferService,
          await this.buildServicesParams(
            TIVO_SERVICES.AiringOfferService,
            this.mFeatureArea
          )
        );
        return (await service.fetchAiringOffers(
          stationId,
          playerCurrentTime
        )) as Array<AiringOffer>;
      },
      options
    );
  }
}
